// Retained for jobs and runtime packages created with the old short-edge contract.
import { TARGET_SHORT_EDGE_BY_TIER, type TargetTier } from './policy';

export const UPSCALER_P_IMAGE_MAX_FACTOR = 8;
export const UPSCALER_MAX_PIXELS = 128_000_000;
export const UPSCALER_CUSTOM_SIZE_FIELD = 'outputShortEdge';

export function getUpscalerSizeTier(shortEdge: number): TargetTier {
  if (!Number.isFinite(shortEdge) || shortEdge <= 0) {
    throw new Error('Output size must be positive.');
  }
  for (const [tier, limit] of Object.entries(TARGET_SHORT_EDGE_BY_TIER)) {
    if (shortEdge <= limit) return tier as TargetTier;
  }
  throw new Error('Output size exceeds 16K.');
}

export function getUpscalerRequestedShortEdge(input: Record<string, unknown>) {
  const custom = input[UPSCALER_CUSTOM_SIZE_FIELD];
  if (custom !== undefined && custom !== 0) {
    if (typeof custom !== 'number' || !Number.isSafeInteger(custom)) {
      throw new Error('Output size must be an integer pixel count.');
    }
    getUpscalerSizeTier(custom);
    return custom;
  }
  const tier = input.targetTier;
  if (
    typeof tier !== 'string' ||
    !Object.hasOwn(TARGET_SHORT_EDGE_BY_TIER, tier)
  ) {
    throw new Error('Unknown output size tier.');
  }
  return TARGET_SHORT_EDGE_BY_TIER[tier as TargetTier];
}

function assertDimensions(image: { width: number; height: number }) {
  if (
    !Number.isSafeInteger(image.width) ||
    !Number.isSafeInteger(image.height) ||
    image.width <= 0 ||
    image.height <= 0
  ) {
    throw new Error('Image dimensions are unavailable.');
  }
}

export function calculateUpscalerOutputSize(
  input: Record<string, unknown>,
  source: { width: number; height: number }
) {
  assertDimensions(source);
  const shortEdge = getUpscalerRequestedShortEdge(input);
  const factor = Math.max(1, shortEdge / Math.min(source.width, source.height));
  const tier = getUpscalerSizeTier(shortEdge);
  const pImage = tier === '8k' || tier === '16k';
  const custom = Boolean(input[UPSCALER_CUSTOM_SIZE_FIELD]);
  let width = Math.round(source.width * factor);
  let height = Math.round(source.height * factor);
  // Preserve the existing P-image Target presets; Factor follows the requested dimensions.
  const targetMegapixels =
    pImage && !custom
      ? tier === '16k'
        ? 128
        : 33
      : Math.ceil((width * height) / 1_000_000);
  if (pImage && !custom) {
    const targetFactor = Math.sqrt(
      (targetMegapixels * 1_000_000) / (source.width * source.height)
    );
    width = Math.round(source.width * targetFactor);
    height = Math.round(source.height * targetFactor);
  }
  return {
    factor,
    width,
    height,
    tier,
    targetMegapixels,
    exceedsLimit: pImage
      ? targetMegapixels * 1_000_000 > UPSCALER_MAX_PIXELS ||
        (custom && factor > UPSCALER_P_IMAGE_MAX_FACTOR)
      : custom && factor > 10,
  };
}
